import gsap from 'gsap';
import { Graphics } from 'pixi.js';
import { gameConfig } from '../config/gameConfig.js';

/**
 * Kill all GSAP tweens on particle children and destroy them.
 * Safe to call mid-burst when a new round starts.
 * @param {import('pixi.js').Container | null | undefined} parent
 */
export function clearParticleLayer(parent) {
  if (!parent || parent.destroyed) return;
  const kids = parent.removeChildren();
  for (let i = 0; i < kids.length; i++) {
    const g = kids[i];
    gsap.killTweensOf(g);
    try {
      g.destroy();
    } catch (_e) {
      /* ignore */
    }
  }
}

/**
 * Lightweight burst of gold motes — used on symbol land, collect, and tier steps.
 * @param {import('pixi.js').Container} parent
 * @param {number} x
 * @param {number} y
 * @param {{ count?: number, color?: number, spread?: number, duration?: number, upwardBias?: number }} [opts]
 */
export function spawnParticleBurst(parent, x, y, opts = {}) {
  if (!parent) return;
  const fx = gameConfig.visualFx ?? {};
  const count = opts.count ?? fx.particleBurstCount ?? 14;
  const color = opts.color ?? gameConfig.theme?.gold ?? 0xe7c463;
  const spread = opts.spread ?? fx.particleBurstSpread ?? 52;
  const life = opts.duration ?? fx.particleBurstDuration ?? 0.58;
  const upward = opts.upwardBias ?? fx.particleBurstUpwardBias ?? 18;

  for (let i = 0; i < count; i++) {
    const g = new Graphics();
    const r = 1.8 + Math.random() * 3.8;
    g.circle(0, 0, r).fill({ color, alpha: 0.92 });
    g.x = x;
    g.y = y;
    g.eventMode = 'none';
    parent.addChild(g);

    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.35;
    const dist = spread * (0.3 + Math.random() * 0.7);
    const tx = x + Math.cos(angle) * dist;
    const ty = y + Math.sin(angle) * dist - upward * Math.random();

    gsap.to(g, {
      x: tx,
      y: ty,
      alpha: 0,
      duration: life * (0.65 + Math.random() * 0.45),
      ease: 'power2.out',
      onComplete: () => {
        if (g.destroyed) return;
        g.parent?.removeChild(g);
        g.destroy();
      },
    });
  }
}

/**
 * Coin-like shower drifting upward (collect / celebration).
 * @param {import('pixi.js').Container} parent
 * @param {number} cx
 * @param {number} cy
 * @param {{ count?: number }} [opts]
 */
export function spawnCoinShower(parent, cx, cy, opts = {}) {
  if (!parent) return;
  const fx = gameConfig.visualFx ?? {};
  const count = opts.count ?? fx.collectCoinCount ?? 22;
  const gold = gameConfig.theme?.goldBright ?? 0xfff8dc;

  for (let i = 0; i < count; i++) {
    const g = new Graphics();
    const w = 4 + Math.random() * 5;
    const h = w * 0.65;
    g.roundRect(-w / 2, -h / 2, w, h, 1).fill({ color: gold, alpha: 0.88 });
    g.x = cx + (Math.random() - 0.5) * 120;
    g.y = cy + (Math.random() - 0.5) * 40;
    g.rotation = Math.random() * Math.PI;
    g.eventMode = 'none';
    parent.addChild(g);

    const delay = Math.random() * 0.18;
    gsap.to(g, {
      y: g.y - 80 - Math.random() * 100,
      x: g.x + (Math.random() - 0.5) * 60,
      alpha: 0,
      rotation: g.rotation + (Math.random() - 0.5) * 2,
      duration: 0.75 + Math.random() * 0.45,
      delay,
      ease: 'power1.out',
      onComplete: () => {
        if (g.destroyed) return;
        g.parent?.removeChild(g);
        g.destroy();
      },
    });
  }
}
